import { useDisclosure } from "@shared/hooks/useDisclosure";
import CheckoutButton from "@shared/components/application/form/CheckoutButton/CheckoutButton.web";
import Skeleton from "@shared/components/ui-kit/feedback/Skeleton/Skeleton.web";
import { Spinner } from "@shared/components/ui-kit/feedback/Spinner/Spinner.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import { ASSIGNED_SEATING } from "@shared/constants";
import useWindowSize from "@shared/hooks/useWindowSize";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import breakpoints from "@shared/theme/props/breakpoints";
import { Z_INDEX } from "@shared/theme/props/zIndexes";
import { useTranslation } from "@shared/utils/internationalization";
import Slide from "@ui-kit/disclosure/Slide/Slide.web";
import Box from "@ui-kit/layout/Box/Box.web";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BEST_AVAILABLE_SLIDE_HEIGHT_PERCENT } from "../../constants";
import { BestAvailableQueryActions } from "../../hooks/useBestAvailableQueryParams";
import { formatSeatingObjectsWithTicketInfo } from "../../utils/utils";
import BestAvailableQuantityInput from "../BestAvailableQuantityInput/BestAvailableQuantityInput.web";
import BestAvailableQuantitySelect from "../BestAvailableQuantitySelect/BestAvailableQuantitySelect.web";
import BestAvailableSeatingBody from "../BestAvailableSeatingBody/BestAvailableSeatingBody.web";
import BestAvailableSeatingCartContainer from "../BestAvailableSeatingCart/BestAvailableSeatingCartContainer/BestAvailableSeatingCartContainer.web";
import { BestAvailableSeatingSlideProps } from "./BestAvailableSeatingSlide.props";

export default function BestAvailableSeatingSlide({
  bestAvailableSeating,
  isOpen,
  minQuantity,
  maxQuantity,
  onContinue,
  isLoading,
  onUpdateQuantity,
  currentQuantity,
  onOpenTicketTypeSelectionDialog,
  onOpen,
  basketFooter,
  hideCheckoutButton,
}: BestAvailableSeatingSlideProps) {
  const { t } = useTranslation("seating");
  const { width } = useWindowSize();
  const {
    selectedSeatIds,
    seatingObjectArray,
    seatingEventData,
    synchronizeSeating,
    hasSeatsWithMultipleTypes,
    hasPasswordProtectedSeats,
    resetSeating,
    stageServiceState,
    isSeatingBasketUpdating,
  } = useSeatingState();
  const isMobile = width && width < breakpoints.sm;
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const {
    isOpen: isCartOpen,
    onClose: onCartClose,
    onOpen: onCartOpen,
  } = useDisclosure();
  const [isFullHeight, setIsFullHeight] = useState(false);
  const lastScrollTop = useRef(0);
  const formattedSelectedItems = useMemo(() => {
    return formatSeatingObjectsWithTicketInfo(
      seatingObjectArray,
      seatingEventData
    );
  }, [seatingObjectArray, seatingEventData]);

  const {
    isOpen: isQuantitySelectOpen,
    onOpen: onQuantitySelectOpen,
    onClose: onQuantitySelectClose,
  } = useDisclosure();

  const {
    isOpen: isCartSlideOpen,
    onOpen: onOpenCartSlide,
    onClose: onCloseCartSlide,
  } = useDisclosure();

  const handleOpenTicketTypeSelectionDialog = () => {
    if (hasSeatsWithMultipleTypes || hasPasswordProtectedSeats) {
      onOpenTicketTypeSelectionDialog();
    } else {
      onQuantitySelectOpen();
    }
  };

  const handleCloseCart = () => {
    onCartClose();
    resetSeating();
    seatingObjectArray.forEach((item) => {
      if (item.name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID) {
        stageServiceState.removeGaSelection(item.id);
      } else {
        stageServiceState.removeSeatSelection(item.id);
      }
      synchronizeSeating();
    });
    setSelectedItemId(null);
  };

  const handleOpenCart = useCallback(() => {
    onCloseCartSlide();
    onCartOpen();
    onOpen();
  }, [onCloseCartSlide, onCartOpen, onOpen]);

  useEffect(() => {
    if (selectedSeatIds.length > 0) {
      if (isOpen) {
        handleOpenCart();
      } else {
        onOpenCartSlide();
        onCartClose();
      }
    }
    if (selectedSeatIds.length === 0) {
      onCloseCartSlide();
      setSelectedItemId(null);
    }

    // Auto-expand slide to full height when cart is open
    if (isCartOpen) {
      setIsFullHeight(true);
    } else {
      setIsFullHeight(false);
    }
  }, [
    selectedSeatIds,
    handleOpenCart,
    onOpenCartSlide,
    onCloseCartSlide,
    onCartClose,
    isOpen,
    isCartOpen,
  ]);
  // this is to expand the cart when there are more than 5 seats selected
  // as the user scrolls down, this will expand the cart to be full height
  // as the user scrolls up, this will collapse the cart to 50vh
  // this is so the user is still able to see their seats on the map when the cart opens,
  // so they can still know where their seats are.
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { currentTarget: target } = e;
      const { scrollTop, scrollHeight, clientHeight } = target;

      const isScrollable = scrollHeight > clientHeight;
      const scrollDelta = scrollTop - lastScrollTop.current;

      if (seatingObjectArray.length >= 5 && isScrollable) {
        // Expand if scrolling down past threshold
        if (scrollTop > 100 && scrollDelta > 0 && !isFullHeight) {
          setIsFullHeight(true);
        }

        // Collapse if user scrolls back to near top (not just any scroll up)
        if (scrollTop < 15 && scrollDelta < 0 && isFullHeight) {
          setIsFullHeight(false);
        }
      } else if (isFullHeight) {
        setIsFullHeight(false);
      }

      lastScrollTop.current = scrollTop;
    },
    [isFullHeight, selectedSeatIds.length]
  );

  if (!isMobile) {
    return null;
  }

  if (isCartSlideOpen && !(isOpen || isCartOpen)) {
    return (
      <Slide
        id="best-available-seating-cart-slide"
        in={isCartSlideOpen}
        direction="bottom"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "10%",
          maxHeight: "10%",
        }}
      >
        <Box
          height="100%"
          bg="bg"
          borderTopRadius="xl"
          shadow="0px -10px 15px -3px rgba(0, 0, 0, 0.1)"
          overflow="hidden"
          p="md"
        >
          <CheckoutButton
            isDisabled={false}
            onCheckout={handleOpenCart}
            quantityToDisplay={seatingObjectArray.length}
            customVerbiage={t("View cart")}
          />
        </Box>
      </Slide>
    );
  }
  return (
    <>
      <Slide
        id="best-available-seating-select-slide"
        in={isOpen && !isCartOpen}
        direction="bottom"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: `${BEST_AVAILABLE_SLIDE_HEIGHT_PERCENT * 100}%`,
          maxHeight: `${BEST_AVAILABLE_SLIDE_HEIGHT_PERCENT * 100}%`,
          // "Remove" element from document flow, so it is not affecting parent container heights
          // which can cause scrollbars to appear that scroll to an empty box.
          display: isCartOpen ? "none" : "block",
        }}
      >
        <Flex
          height="100%"
          flexDirection="column"
          bg="bg"
          borderTopRadius="xl"
          shadow="0px -10px 15px -3px rgba(0, 0, 0, 0.1)"
          overflow="hidden"
        >
          {isSeatingBasketUpdating && (
            <Box
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {selectedSeatIds.length === 0 ? (
                <BestAvailableSeatingSlide.LoadingSkeleton />
              ) : (
                <BestAvailableSeatingSlide.LoadingSpinner />
              )}
            </Box>
          )}
          {!isSeatingBasketUpdating && !isCartOpen && (
            <>
              <Flex
                direction="column"
                gap="md"
                padding="md"
                position="sticky"
                top="none"
                bg="bg"
              >
                <Text fontWeight="bold">{t("Select seats")}</Text>
                <BestAvailableQuantityInput
                  value={currentQuantity}
                  onOpenTypeQuantitySelect={handleOpenTicketTypeSelectionDialog}
                  asButton
                />
              </Flex>
              <BestAvailableSeatingBody
                bestAvailableSeating={bestAvailableSeating}
                isLoading={isLoading}
                onContinue={onContinue}
                selectedItemId={selectedItemId}
                setSelectedItemId={setSelectedItemId}
                selectedSeatIds={selectedSeatIds}
                updateSelectedSeats={synchronizeSeating}
              />
              {basketFooter}
            </>
          )}
        </Flex>
        <BestAvailableQuantitySelect.Drawer
          isOpen={isQuantitySelectOpen}
          onClose={onQuantitySelectClose}
          onSubmit={(quantity: number) => {
            onUpdateQuantity({
              type: BestAvailableQueryActions.QUANTITY,
              payload: { quantity },
            });
            onQuantitySelectClose();
          }}
          minQuantity={minQuantity}
          maxQuantity={maxQuantity}
        />
      </Slide>
      {selectedSeatIds.length > 0 && (
        <Slide
          id="best-available-seating-selected-seats-slide"
          in={isOpen && isCartOpen}
          direction="bottom"
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: isFullHeight
              ? "60%"
              : `${BEST_AVAILABLE_SLIDE_HEIGHT_PERCENT * 100}%`,
            maxHeight: isFullHeight
              ? "60%"
              : `${BEST_AVAILABLE_SLIDE_HEIGHT_PERCENT * 100}%`,
            transition: "height 0.3s ease-in-out",
            zIndex: Z_INDEX.drawer,
          }}
        >
          <Flex
            bg="bg"
            borderTopRadius="xl"
            shadow="0px -10px 15px -3px rgba(0, 0, 0, 0.1)"
            height="100%"
            flexDirection="column"
            transition="height 0.3s ease-in-out"
            overflow="hidden"
            padding="none"
            width="100%"
          >
            {isSeatingBasketUpdating ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <BestAvailableSeatingSlide.LoadingSpinner />
              </Box>
            ) : (
              <Box
                flex="1"
                // eslint-disable-next-line showpass-custom/no-hardcoded-style-values -- Required for flex overflow pattern
                minHeight="0"
                overflowY="auto"
                onScroll={handleScroll}
              >
                <BestAvailableSeatingCartContainer.Content
                  seatingObjectArray={formattedSelectedItems}
                  onClose={handleCloseCart}
                  onContinue={onContinue}
                  isDisabled={!selectedItemId && selectedSeatIds.length === 0}
                  hideCheckoutButton={hideCheckoutButton}
                />
              </Box>
            )}
          </Flex>
        </Slide>
      )}
    </>
  );
}

BestAvailableSeatingSlide.LoadingSpinner = function LoadingSpinner() {
  return (
    <Spinner
      color="primary.600"
      emptyColor="gray.400"
      speed="0.65s"
      size="3xl"
      thickness="10px"
    />
  );
};

BestAvailableSeatingSlide.LoadingSkeleton = function LoadingSkeleton() {
  return <Skeleton height="100%" width="100%" />;
};
